import {
  CheckCheck,
  Check,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDollarSign,
  CircleSlash,
  Eye,
  Loader,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import type { ReactNode } from 'react';
import {
  type DayCloseSessionStatus,
  ENUM_LABELS,
  type PaymentStatus,
  type ReportStatus,
  type RoleSummary,
  type WhatsappShareStatus,
} from '#shared/types';
import { Badge, type BadgeTone } from './Badge';

/**
 * The single place a domain status becomes a visual.
 *
 * Two reasons this exists rather than each screen mapping its own tones:
 *
 * 1. **Accessibility.** A status must carry at least two of {colour, shape,
 *    text}. Colour alone fails for roughly one in twelve men, and it fails
 *    completely in a printed or photocopied screenshot. Going through this
 *    component makes the icon structural rather than something a reviewer has
 *    to remember to ask for.
 *
 * 2. **Consistency.** The payment and report tone maps are needed on the
 *    patient list, the patient detail and the worklist. Three copies of a mapping is three chances for "partial" to be amber on
 *    one screen and grey on another.
 *
 * Labels always come from `ENUM_LABELS`, so the wording matches the select
 * options that set them.
 */

interface StatusConfig {
  tone: BadgeTone;
  icon: ReactNode;
}

const REPORT_STATUS: Record<ReportStatus, StatusConfig> = {
  // Registered work has not been started — grey, not red. It is a queue, not a
  // problem; overdue is what makes it urgent, and that is a separate signal.
  registered: { tone: 'draft', icon: <CircleDashed /> },
  in_progress: { tone: 'info', icon: <Loader /> },
  completed: { tone: 'success', icon: <CircleCheck /> },
};

const PAYMENT_STATUS: Record<PaymentStatus, StatusConfig> = {
  paid: { tone: 'success', icon: <CircleCheck /> },
  partial: { tone: 'warning', icon: <CircleDollarSign /> },
  pending: { tone: 'critical', icon: <CircleSlash /> },
};

/**
 * Where a WhatsApp send has got to.
 *
 * `sent` and `delivered` are deliberately different words *and* different
 * icons. "Sent" means Meta accepted it; "Delivered" means the handset
 * acknowledged it. Collapsing the two would let the screen claim a report
 * reached a patient on the strength of an API call — which is exactly what a
 * misconfigured webhook produces, and exactly the thing nobody should have to
 * discover by ringing the patient.
 */
const SHARE_STATUS: Record<WhatsappShareStatus, StatusConfig> = {
  sending: { tone: 'info', icon: <Loader /> },
  sent: { tone: 'info', icon: <Check /> },
  delivered: { tone: 'success', icon: <CheckCheck /> },
  read: { tone: 'success', icon: <Eye /> },
  failed: { tone: 'critical', icon: <CircleAlert /> },
};

export function ShareStatusBadge({ status }: { status: WhatsappShareStatus }) {
  const config = SHARE_STATUS[status];
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {ENUM_LABELS.whatsappShareStatus[status]}
    </Badge>
  );
}

export function ReportStatusBadge({ status }: { status: ReportStatus }) {
  const config = REPORT_STATUS[status];
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {ENUM_LABELS.reportStatus[status]}
    </Badge>
  );
}

export function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  const config = PAYMENT_STATUS[status];
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {ENUM_LABELS.paymentStatus[status]}
    </Badge>
  );
}

/**
 * Where a login session stands at the end of the day. `missing` is amber, not
 * red: a forgotten submission is a question to ask, not a discrepancy found.
 */
const DAY_CLOSE_STATUS: Record<DayCloseSessionStatus, StatusConfig & { label: string }> = {
  open: { tone: 'info', icon: <Loader />, label: 'Signed in' },
  submitted: { tone: 'success', icon: <CircleCheck />, label: 'Submitted' },
  missing: { tone: 'warning', icon: <CircleAlert />, label: 'Not submitted' },
};

export function DayCloseStatusBadge({ status }: { status: DayCloseSessionStatus }) {
  const config = DAY_CLOSE_STATUS[status];
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {config.label}
    </Badge>
  );
}

/**
 * A staff role.
 *
 * Roles are named by the lab, so there is no fixed tone per role. The one
 * distinction worth seeing at a glance is the admin role, which holds every
 * permission.
 */
export function RoleBadge({ role }: { role: RoleSummary }) {
  return (
    <Badge
      tone={role.is_super ? 'info' : 'neutral'}
      icon={role.is_super ? <ShieldCheck /> : <UserRound />}
    >
      {role.name}
    </Badge>
  );
}
